import React, { useState } from 'react';
import styles from './message-vote-buttons.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { updateMessage } from 'utils/smartchat/messages-api';
import { useUser } from 'contexts';

export enum Vote {
  UPVOTE = 'upvote',
  DOWNVOTE = 'downvote',
}

export type VoteType = Vote | null;

export interface MessageVoteButtonsProps {
  messageId?: string;
  conversationId?: string;
  initialVote?: VoteType;
  initialMetadata?: Record<string, string>;
  className?: string;
}

export default function MessageVoteButtons({ messageId, conversationId, initialVote = null, initialMetadata = {}, className }: MessageVoteButtonsProps) {
  const [vote, setVote] = useState<VoteType>(initialVote);
  const [metadata, setMetadata] = useState<Record<string, string>>(initialMetadata);
  const [isSaving, setIsSaving] = useState(false);
  const { user, isAuthenticated } = useUser();

  const handleVote = async (newVote: VoteType) => {
    const prevVote = vote;
    const prevMetadata = metadata;

    // Untoggle the vote if the same button is clicked again
    if (newVote === vote) {
      newVote = null;
    }
    
    // Save the user's vote to the message metadata
    if (isAuthenticated && messageId && conversationId) {
      setIsSaving(true);
      try {
        // Filter out any existing votes
        const existingTags = metadata?.tags ? JSON.parse(metadata.tags) : []; 
        const filteredTags = existingTags.filter(tag => tag !== Vote.UPVOTE && tag !== Vote.DOWNVOTE);
        
        // Add vote and serialize the metadata 
        const tags = newVote ? [...filteredTags, newVote] : filteredTags;
        const updatedMetadata = { ...metadata, tags: JSON.stringify(tags) };
        
        // Optimistic update;ensure UI transition is smooth
        setVote(newVote);
        setMetadata(updatedMetadata);
        
        await updateMessage({
          jwt: user?.jwt,
          conversationId,
          messageId,
          metadata: updatedMetadata,
        });
        
        console.log(`Vote ${newVote} saved for message ${messageId}`);
      } catch (error) {
        console.error('Error saving vote:', error);
        // Revert to previous vote if API fails
        setVote(prevVote);
        setMetadata(prevMetadata);
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className={buildClassName(styles['vote-buttons'], className)}>
      {vote !== Vote.DOWNVOTE && (
        <button
          className={buildClassName(styles['vote-button'], vote === Vote.UPVOTE ? styles.active : '')}
          onClick={() => handleVote(Vote.UPVOTE)}
          disabled={isSaving}
          aria-label="Upvote this response"
          data-testid="msg-upvote-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill={vote === Vote.UPVOTE ? 'currentColor' : 'none'}>
            <path d="M13.5537 6.98242V6.98145C13.6212 7.0505 13.6638 7.14881 13.667 7.25391L13.6553 7.38184L13.0996 10.0996L12.9766 10.7002H17.3184C17.685 10.7003 18 11.0094 18 11.4004V12.5996C18 12.6888 17.9833 12.7714 17.9512 12.8555L16.1719 17.0732L16.1699 17.0781C16.0667 17.3297 15.8255 17.4998 15.5459 17.5H10.2275C9.86085 17.5 9.5459 17.1909 9.5459 16.7998V10.7998C9.54595 10.6035 9.62173 10.4315 9.74512 10.3096L9.75098 10.3047L13.2832 6.71094L13.5537 6.98242ZM7.36328 11.2998V17.5H6V11.2998H7.36328Z" stroke="currentColor" />
          </svg>
        </button>
      )}
      {vote !== Vote.UPVOTE && (
        <button
          className={buildClassName(styles['vote-button'], vote === Vote.DOWNVOTE ? styles.active : '')}
          onClick={() => handleVote(Vote.DOWNVOTE)}
          disabled={isSaving}
          aria-label="Downvote this response"
          data-testid="msg-downvote-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill={vote === Vote.DOWNVOTE ? 'currentColor' : 'none'}>
            <path d="M10.4463 17.0176V17.0186C10.3788 16.9495 10.3362 16.8512 10.333 16.7461L10.3447 16.6182L10.9004 13.9004L11.0234 13.2998H6.68164C6.31503 13.2997 6 12.9906 6 12.5996V11.4004C6 11.3112 6.01675 11.2286 6.04883 11.1445L7.82812 6.92676L7.83008 6.92188C7.93331 6.67033 8.1745 6.50018 8.4541 6.5H13.7725C14.1391 6.5 14.4541 6.80911 14.4541 7.2002V13.2002C14.4541 13.3965 14.3783 13.5685 14.2549 13.6904L14.249 13.6953L10.7168 17.2891L10.4463 17.0176ZM16.6367 12.7002V6.5H18V12.7002H16.6367Z" stroke="currentColor" />
          </svg>
        </button>
      )}
    </div>
  );
}

